// @flow
/*
 *  Copyright 2018 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 *
 */

import getConfigApps from '../config';

class CCDDTMAnalytics {
	reportDTMEvent(reportedEvent: string, eventData: Object, eventType: string, appLauncherPresent?: boolean) {
		let domain = 'creativecloud.adobe.com';
		switch (getConfigApps('env')) {
			case 'local':
				domain = 'stage.creativecloud.adobe.com';
				break;
			case 'dev':
				domain = 'dev.creativecloud.adobe.com';
				break;
			case 'stage':
				domain = 'stage.creativecloud.adobe.com';
				break;
			case 'prod':
				domain = 'creativecloud.adobe.com';
				break;
		}
		const eventName = `${domain}:${reportedEvent}`;

		if (window.cchome && window.cchome.util && window.cchome.util.hasFeature && window.cchome.util.hasFeature('temp_enable_cchome_apps_logs')) {
			console.log(`DTMEvent: eventName-${eventName} ----- eventData-${JSON.stringify(eventData)} ----- eventType-${eventType} ----- appLauncherPresent-${(!!appLauncherPresent).toString()}`);
		}

		const digitalData: Object = window.digitalData;
		if (typeof digitalData === 'undefined') {
			return;
		}

		digitalData._set(
			'digitalData.primaryProduct.productInfo.productID',
			eventData.productId
		);

		if (eventData.surveyResults) {
			digitalData._set(
				'digitalData.form.response.list.joined.formInfo.name',
				eventData.surveyResults
			);
		}

		digitalData._set(
			'digitalData.primaryUser.primaryProfile.profileInfo.entitlementCreativeCloud',
			eventData.subscriptionType
		);

		if (eventName.toLowerCase().indexOf('installaam') !== -1) {
			digitalData._set(
				'digitalData.primaryProduct.productInfo.productName',
				`CCM App Download: InstallAAM:${eventData.productId}`
			);
		}
		if (eventName.toLowerCase().indexOf('aaminstalled') !== -1) {
			digitalData._set(
				'digitalData.primaryProduct.productInfo.productName',
				`CCM App Download: AAMInstalled:${eventData.productId}`
			);
		}

		switch (eventType) {
			case 'state' :
				this.sendStateEvents(eventName, eventData, appLauncherPresent);
				break;
			case 'event' :
				this.sendUserActionEvent(eventName);
				break;
		}

		digitalData._delete('digitalData.primaryProduct.productInfo.productID');
		digitalData._delete('digitalData.primaryUser.primaryProfile.profileInfo.entitlementCreativeCloud');
		if (eventData.surveyResults) {
			digitalData._delete('digitalData.form.response.list.joined.formInfo.name');
		}
	}

	sendStateEvents(eventName: string, eventData: Object, appLauncherPresent: ?boolean) {
		const digitalData: Object = window.digitalData;
		if (appLauncherPresent) {
			digitalData._set(
				'digitalData.component[0].componentInfo.name',
				'App Launcher'
			);
		}

		digitalData._set(
			'digitalData.page.pageInfo.pageName',
			eventName
		); // varies as per the page url or download pages


		window._satellite.track('state', {
			digitalData: digitalData._snapshot()
		});

		if (eventData.appLauncherPresent) {
			digitalData._delete('digitalData.component[0].componentInfo.name');
		}
		digitalData._delete('digitalData.page.pageInfo.pageName');
	}

	sendUserActionEvent(eventName: string) {
		const digitalData: Object = window.digitalData;
		digitalData._set(
			'digitalData.primaryEvent.eventInfo.eventName',
			eventName
		);

		window._satellite.track('event', {
			digitalData: digitalData._snapshot()
		});

		digitalData._delete(
			'digitalData.primaryEvent.eventInfo.eventName'
		);
	}

	setLanguageField() {
		const digitalData: Object = window.digitalData;
		if (typeof digitalData === 'undefined') {
			return;
		}

		digitalData._set(
			'digitalData.page.pageInfo.language',
			window.adobeIMS ? window.adobeIMS.getLocale() : window.adobeid.locale
		);
	}
};

export default new CCDDTMAnalytics();
